import type { DataChartModel, DataChartSeries } from "@repo/ui/charts/dataChart";

import { leaderboardPalettes } from "./widgetChartColors";

type Row = Record<string, unknown>;
type Definition = {
	kind?: DataChartModel["kind"];
	category: string;
	values: string[];
	group?: string;
	horizontal?: boolean;
	stack?: boolean;
	percent?: boolean;
	lines?: string[];
	right?: string[];
	rightLabel?: string;
	paletteSeed?: number;
};

// Field names and series follow MVC/Views/MyView/Widgets, not numeric-column guessing.
const pies: Record<string, [string, string]> = {
	GasElectric: ["Commodity", "Sales"],
	EnrollCommodity: ["Commodity", "Sales"],
	CarrierTypes: ["Category", "Sales"],
	ContractTerm: ["Term", "Sales"],
	EntriesCommoditySalesType: ["SalesType", "Entries"],
	CreditCheckResults: ["Name", "Count"],
	EntriesDeposits: ["Value", "Entries"],
	FullVsPartial: ["Status", "Sales"],
	LeadTypes: ["Type", "Leads"],
	OnListSales: ["Category", "Sales"],
	POSIDMatches: ["Category", "Sales"],
	PricingType: ["PricingType", "Sales"],
	PricingStatus: ["PricingStatus", "Sales"],
	SaleStatus: ["Status", "Sales"],
	SeniorCitizenPercent: ["Title", "Value"],
	SurveyStatus: ["Status", "Surveys"],
	TabletUtilization: ["Category", "Count"],
	TPVTypes: ["Category", "Count"],
	TPVStatus: ["Category", "Sales"],
};
const definitions: Record<string, Definition> = {
	...Object.fromEntries(
		Object.entries(pies).map(([name, [category, value]]) => [name, { kind: "pie", category, values: [value] }]),
	),
	EntriesPercentToGoal: { kind: "donut", category: "Status", values: ["Entries"] },
	BillImageTPVOutcomes: { kind: "donut", category: "Category", values: ["WithBillImages", "WithoutBillImages"] },
	CustomerAges: { category: "AgeRange", values: ["Sales"] },
	CreditScoreBuckets: { category: "Bucket", values: ["Count"] },
	LeadActivities: { category: "Disposition", values: ["Count"] },
	LeadDispositions: { category: "Disposition", values: ["Count"] },
	TurfDispositions: { category: "Disposition", values: ["Count"] },
	EntriesByMarket: { category: "Market", values: ["Entries"], group: "Product", horizontal: true },
	EntriesByLocation: { category: "Location", values: ["Entries"], group: "Product", horizontal: true },
	VendorLeaderboard: { category: "Vendor", values: ["Sales"], group: "State", stack: true, paletteSeed: 38 },
	StateLeaderboard: { category: "State", values: ["Sales"], group: "Vendor", stack: true, paletteSeed: 22 },
	UtilityLeaderboard: { category: "Utility_Code", values: ["Sales"], group: "Vendor", stack: true, paletteSeed: 96 },
	ManagerLeaderboard: { category: "Vendor", values: ["Sales"], group: "Manager", stack: true, paletteSeed: 117 },
	OfficeLeaderboard: { category: "Vendor", values: ["Sales"], group: "Office", stack: true, paletteSeed: 117 },
	TPVStatusByVendor: {
		category: "VendorName",
		values: ["GoodSales", "NoSales"],
		horizontal: true,
		stack: true,
		percent: true,
	},
	SalesVsGoal: {
		category: "Period",
		values: ["ElectricSales", "GasSales", "Goal", "CumSales", "CumGoal"],
		stack: true,
		lines: ["Goal", "CumSales", "CumGoal"],
		right: ["CumSales", "CumGoal"],
		rightLabel: "Cumulative",
	},
	PartnerAPIPerformance: {
		category: "Operation",
		values: ["Successes", "Errors", "Timeouts", "Avg_Duration"],
		lines: ["Avg_Duration"],
		right: ["Avg_Duration"],
		rightLabel: "Seconds",
	},
	AvgDailyRepSales: {
		kind: "scatter",
		category: "Branch",
		values: ["AvgActiveReps", "AvgDailySales"],
		group: "Vendor",
	},
	AvgDailyLocationRepSales: {
		kind: "scatter",
		category: "Location",
		values: ["AvgActiveReps", "AvgDailySales"],
		group: "Partner",
	},
};

// Dynamic results can retain PascalCase while normal API objects are camelCased.
function field(row: Row, name: string): unknown {
	const key = Object.keys(row).find((candidate) => candidate.toLowerCase() === name.toLowerCase());
	return key === undefined ? undefined : row[key];
}
function groupLabel(value: unknown): string | undefined {
	if (value === null) return "Unassigned";
	if (typeof value === "string" || typeof value === "number") return String(value);
	// Location scatter results include a Partner object rather than just its name.
	if (value && typeof value === "object") {
		const name = field(value as Row, "Name");
		if (typeof name === "string") return name;
	}
	return undefined;
}
function settings(json?: string | null): Row {
	try {
		const value: unknown = JSON.parse(json || "{}");
		return value && typeof value === "object" && !Array.isArray(value) ? (value as Row) : {};
	} catch {
		return {};
	}
}
function enabled(value: unknown) {
	return value === true || value === "true";
}
function numeric(value: unknown): number | null {
	// Missing measures remain gaps; identifiers and formatted percentages are never inferred as measures.
	if (typeof value !== "number" && !(typeof value === "string" && value.trim() !== "")) return null;
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
}
function color(value: unknown): string | undefined {
	// Only colors, never arbitrary CSS, enter shadcn's generated color stylesheet.
	return typeof value === "string" && /^(#[\da-f]{3,8}|[a-z]+)$/i.test(value) ? value : undefined;
}
const names: Record<string, string> = {
	CumSales: "Cumulative Sales",
	CumGoal: "Cumulative Goal",
	Avg_Duration: "Average Request Duration",
};
function label(name: string) {
	return names[name] ?? name.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
}

export function parseWidgetChart(
	widgetName: string,
	rows: Row[],
	configJson?: string | null,
): DataChartModel | undefined {
	if (widgetName === "SalesDayTime" && rows.length && !enabled(field(settings(configJson), "DisplayAsGrid"))) {
		const data = rows.flatMap((row) => {
			const day = numeric(field(row, "DayOfWeek"));
			const hour = numeric(field(row, "Hour"));
			const value = numeric(field(row, "Sales"));
			if (
				day === null ||
				hour === null ||
				value === null ||
				!Number.isInteger(day) ||
				!Number.isInteger(hour) ||
				day < 0 ||
				day > 6 ||
				hour < 7 ||
				hour > 21 ||
				value < 0
			)
				return [];
			return [{ x: hour - 7, y: day, value, average: numeric(field(row, "AvgSales")) }];
		});
		return {
			kind: "heatmap",
			data,
			series: [],
			heatmap: {
				columns: Array.from({ length: 15 }, (_, i) => {
					const hour = i + 7;
					return `${hour % 12 || 12}${hour < 12 ? "a" : "p"}`;
				}),
				rows: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
			},
		};
	}
	const original = Object.hasOwn(definitions, widgetName) ? definitions[widgetName] : undefined;
	if (!original || !rows.length) return undefined;
	const config = settings(configJson);
	// Grid queries can contain totals or entirely different columns. Keep their saved representation.
	if (
		enabled(field(config, "DisplayAsGrid")) ||
		(widgetName === "LeadActivities" && enabled(field(config, "DisplayAsLeaderboard")))
	)
		return undefined;
	const definition =
		widgetName === "OfficeLeaderboard" && enabled(field(config, "DisplayElectricUsage"))
			? { ...original, values: ["Sales", "ElectricUsage"] }
			: original;
	const { category, values, group } = definition;
	const kind = definition.kind ?? "cartesian";
	const circular = kind === "pie" || kind === "donut";
	if (
		rows.some((row) => {
			const categoryValue = field(row, category);
			return typeof categoryValue !== "string" && typeof categoryValue !== "number";
		})
	)
		return undefined;
	if (values.some((value) => !rows.some((row) => numeric(field(row, value)) !== null))) return undefined;
	if (
		circular &&
		rows.some((row) => values.some((value) => numeric(field(row, value)) === null || Number(field(row, value)) < 0))
	)
		return undefined;
	if (group && rows.some((row) => groupLabel(field(row, group)) === undefined)) return undefined;
	const groups = group ? [...new Set(rows.map((row) => groupLabel(field(row, group))))] : [""];
	const palette = definition.paletteSeed === undefined ? undefined : leaderboardPalettes[definition.paletteSeed];
	// Kendo groups ascending before assigning colors, independently of category ranking.
	if (palette) groups.sort((a, b) => a.localeCompare(b));
	const series: DataChartSeries[] = groups.flatMap((groupName, groupIndex) =>
		(kind === "scatter" ? [values[1]!] : values).map((value, valueIndex) => ({
			key: `s${groupIndex}_${valueIndex}`,
			label: group
				? values.length === 1 || kind === "scatter"
					? groupName
					: `${groupName} · ${label(value)}`
				: label(value),
			kind: definition.lines?.includes(value) ? "line" : "bar",
			axis: definition.right?.includes(value) ? "right" : undefined,
			stack: definition.stack && !definition.lines?.includes(value) ? "total" : undefined,
			...(palette ? { color: palette[groupIndex % palette.length], barLabel: groupName } : {}),
		})),
	);
	if (kind === "scatter") {
		if (rows.some((row) => values.some((value) => numeric(field(row, value)) === null))) return undefined;
		return {
			kind,
			series,
			xLabel: "Avg. Active Reps",
			yLabel: "Avg. Daily Sales",
			data: rows.map((row) => ({
				category: `${groupLabel(field(row, group!))} · ${String(field(row, category))}`,
				x: numeric(field(row, values[0]!)),
				y: numeric(field(row, values[1]!)),
				group: `s${groups.indexOf(groupLabel(field(row, group!)))}_0`,
			})),
		};
	}
	const data: DataChartModel["data"] = [];
	const categories = new Map<string | number, DataChartModel["data"][number]>();
	for (const row of rows) {
		const categoryValue = field(row, category) as string | number;
		let point = group ? categories.get(categoryValue) : undefined;
		if (!point) {
			point = { category: categoryValue };
			if (group) {
				series.forEach((item) => {
					point![item.key] = null;
				});
				categories.set(categoryValue, point);
			}
			data.push(point);
		}
		const groupIndex = group ? groups.indexOf(groupLabel(field(row, group))) : 0;
		values.forEach((value, index) => {
			const key = `s${groupIndex}_${index}`;
			const amount = numeric(field(row, value));
			point[key] = amount === null ? (point[key] ?? null) : Number(point[key] ?? 0) + amount;
		});
		const fill = color(field(row, "Color"));
		if (fill) point.fill = fill;
		const fullPeriod = field(row, "FullPeriod");
		if (typeof fullPeriod === "string") point.tooltipLabel = fullPeriod;
	}
	// A zero-total pie has no sectors to interact with. Show the data table instead.
	if (circular && !series.some((item) => data.some((row) => Number(row[item.key]) > 0))) return undefined;
	if (palette) {
		for (const point of data)
			point.categoryTotal = series.reduce((sum, item) => sum + Number(point[item.key] ?? 0), 0);
		data.sort((a, b) => Number(b.categoryTotal) - Number(a.categoryTotal));
	}
	return {
		kind,
		data,
		series,
		horizontal: definition.horizontal,
		percent: definition.percent,
		rightLabel: definition.rightLabel,
		...(palette ? { showLegend: false, categoryTotals: true, barGradient: true, categoryWindow: 5 } : {}),
	};
}
